'use client'

import {useContext, useEffect, useMemo, useState} from 'react'
import {PublicEntryChromeContext} from './PublicEntryChromeContext'
import {usePathname, useRouter} from 'next/navigation'
import type {PublicPrivateBookingOffer} from '../../../services/public-private-bookings.service'
import type {PublicScheduleEvent} from '../../../services/public-schedule.service'
import type {PublicPartnerProfile} from '../../../services/public-profile.service'
import {useTranslation} from '../../../hooks/useTranslation'
import {usePartnerStaffAccess} from '../../../hooks/usePartnerStaffAccess'
import {useAuth} from '@fitro/customer-host/auth'
import {canAccessAdminDashboard} from '../../../lib/staff-access'
import {PublicOfferTabs} from './PublicOfferTabs'
import type {PublicOfferPage} from './PublicOfferTabs'
import {PublicEventsContent} from './PublicEventsContent'
import {
    buildAuthenticatedPrivateBookingUrl,
    PublicPrivateBookingFlow,
    type PublicPrivateBookingIntent,
} from './PublicPrivateBookingsPage'
import {supportsPublicOnlineBooking} from '../../../services/public-private-bookings.service'
import {PUBLIC_SITE_CONTENT_CLASS_NAME, PublicSiteShell} from './PublicSiteShell'
import {PublicSchedulePreview} from './PublicSchedulePreview'
import type {LocalWeekRange} from './publicEntryDates'
import {publicEntryStyles} from './publicEntryStyles'
import {hasPublicProfileDetails} from './publicProfileVisibility'
import {PublicEntryLoading} from './PublicEntryLoading'
import {PublicEventDetailsPage} from './PublicEventDetailsPage'
import {getAuthenticatedEventDetailsHref, openClassOverCalendar} from './publicEventLinks'
import {ConfirmDialog} from '../../ui/Modal/ConfirmDialog'

type ClientPublicEntryViewProps = {
    showBookingSummary?: boolean
    events: PublicScheduleEvent[]
    initialEvent?: PublicScheduleEvent | null
    initialOfferPage?: PublicOfferPage
    partnerName: string | null
    partnerSlug?: string
    profile?: PublicPartnerProfile | null
    privateBookingOffers?: PublicPrivateBookingOffer[]
    publicEvents?: PublicScheduleEvent[]
    redirectUrl: string
    weekRange: LocalWeekRange
}

export function ClientPublicEntryView({
    events,
    initialEvent = null,
    initialOfferPage = 'schedule',
    partnerName,
    partnerSlug = '',
    profile,
    privateBookingOffers = [],
    publicEvents = [],
    redirectUrl,
    showBookingSummary = true,
    weekRange,
}: ClientPublicEntryViewProps) {
    const {t} = useTranslation('landing')
    const inheritedChrome = useContext(PublicEntryChromeContext)
    const {isLoaded, isSignedIn} = useAuth()
    const staffAccess = usePartnerStaffAccess({enabled: Boolean(isLoaded && isSignedIn)})
    const canOpenAdminDashboard = canAccessAdminDashboard(staffAccess.data)
    const router = useRouter()
    const [bookingIntent, setBookingIntent] = useState<PublicPrivateBookingIntent | null>(null)
    const pathname = usePathname()
    const publicPrivateBookingOffers = privateBookingOffers.filter(supportsPublicOnlineBooking)
    const canShowPrivateBookings = publicPrivateBookingOffers.length > 0
    const visiblePublicEvents = useMemo(() => {
        if (!initialEvent) return publicEvents
        return [
            initialEvent,
            ...publicEvents.filter((event) => event.id !== initialEvent.id),
        ]
    }, [initialEvent, publicEvents])
    const canShowEvents = visiblePublicEvents.length > 0
    const resolvedInitialOfferPage = initialOfferPage === 'privateBookings' && !canShowPrivateBookings
        ? 'schedule'
        : initialOfferPage === 'events' && !canShowEvents
            ? 'schedule'
            : initialOfferPage
    const displayPartnerName = partnerName?.trim() || t('publicEntry.fallbackStudioName')
    const signedInRedirectUrl = initialEvent
        ? getAuthenticatedEventDetailsHref(initialEvent)
        : initialOfferPage === 'privateBookings'
            ? '/calendar/individual'
            : redirectUrl
    const isStaffPublicPreview = Boolean(isSignedIn && canOpenAdminDashboard)

    function continueAuthenticatedBooking() {
        if (!bookingIntent) return
        const targetUrl = buildAuthenticatedPrivateBookingUrl(bookingIntent)
        setBookingIntent(null)
        router.push(targetUrl)
    }

    useEffect(() => {
        if (!isLoaded || !isSignedIn || staffAccess.isLoading || canOpenAdminDashboard) return
        // A linked class opens over the calendar of its day, not over this page left empty behind it.
        if (initialEvent && openClassOverCalendar(initialEvent, (path) => router.replace(path))) return
        router.replace(signedInRedirectUrl)
    }, [canOpenAdminDashboard, initialEvent, isLoaded, isSignedIn, router, signedInRedirectUrl, staffAccess.isLoading])

    useEffect(() => {
        if (initialEvent) return
        if (initialOfferPage === resolvedInitialOfferPage) return
        router.replace('/')
    }, [initialEvent, initialOfferPage, resolvedInitialOfferPage, router])

    if (!isLoaded || (isSignedIn && (staffAccess.isLoading || !canOpenAdminDashboard))) {
        return <PublicEntryLoading page={resolvedInitialOfferPage} />
    }

    return (
        <PublicSiteShell activePage="schedule" partnerName={partnerName ?? ''} redirectUrl={signedInRedirectUrl} showAboutPage={hasPublicProfileDetails(profile)}>
            <div data-testid="client-public-entry" data-redirect-url={signedInRedirectUrl} className={`${PUBLIC_SITE_CONTENT_CLASS_NAME} ${inheritedChrome ? (initialEvent || pathname.startsWith('/wydarzenia/') ? publicEntryStyles.offerDetailPadding : publicEntryStyles.offerPagePadding) : publicEntryStyles.pagePadding}`}>
                {!initialEvent ? <h1 className="sr-only">{t('publicEntry.pageTitle', {partnerName: displayPartnerName})}</h1> : null}
                <PublicOfferTabs
                    activePage={resolvedInitialOfferPage}
                    compactBottomSpacing={Boolean(initialEvent || pathname.startsWith('/wydarzenia/'))}
                    showEvents={canShowEvents}
                    showPrivateBookings={canShowPrivateBookings}
                />
                <div data-public-entry-offer-content>
                    <div data-public-entry-base-content>
                        {initialEvent ? (
                            <PublicEventDetailsPage
                                backHref="/wydarzenia"
                                event={initialEvent}
                                partnerSlug={partnerSlug}
                            />
                        ) : (
                            <>
                                {resolvedInitialOfferPage === 'privateBookings' ? (
                                    <section aria-labelledby="public-private-bookings-content">
                                        <h2 id="public-private-bookings-content" className="sr-only">
                                            {t('publicEntry.privateBookings.title')}
                                        </h2>
                                        <PublicPrivateBookingFlow
                                            offers={publicPrivateBookingOffers}
                                            onAuthenticatedBookingIntent={isStaffPublicPreview ? setBookingIntent : undefined}
                                            partnerSlug={partnerSlug}
                                            profile={profile ?? null}
                                        />
                                    </section>
                                ) : resolvedInitialOfferPage === 'events' ? (
                                    <PublicEventsContent events={visiblePublicEvents} />
                                ) : (
                                    <PublicSchedulePreview showBookingSummary={showBookingSummary} events={events} partnerSlug={partnerSlug} weekRange={weekRange} />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            <ConfirmDialog
                isOpen={bookingIntent !== null}
                onClose={() => setBookingIntent(null)}
                onConfirm={continueAuthenticatedBooking}
                title={t('publicEntry.privateBookings.previewMode.dialogTitle')}
                description={t('publicEntry.privateBookings.previewMode.dialogDescription')}
                confirmLabel={t('publicEntry.privateBookings.previewMode.continue')}
                cancelLabel={t('publicEntry.privateBookings.previewMode.stay')}
                variant="primary"
                modalSize="small"
            />
        </PublicSiteShell>
    )
}
